// Lot M6 · Pilotage Mandats — Actions
// Direction visuelle : tableau dense, ivoire, séparateurs 1px, lime
// réservé aux actions prioritaires. Scoped à ce composant uniquement.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import pilotageMandates from "@/lib/api/pilotageMandates";

const SEVERITY_META = {
  P0: { rank: 0, label: "P0", bar: "bg-[#111]"       },
  P1: { rank: 1, label: "P1", bar: "bg-[#1a1a1a]"    },
  P2: { rank: 2, label: "P2", bar: "bg-black/35"     },
  P3: { rank: 3, label: "P3", bar: "bg-black/15"     },
};

const fmtKm   = (v) => (v == null ? null : new Intl.NumberFormat("fr-FR").format(v) + " km");
const fmtEur  = (v, cur = "EUR") => (v == null ? null :
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur,
                                     maximumFractionDigits: 0 }).format(v));
const fmtNum  = (v) => (v == null ? null : new Intl.NumberFormat("fr-FR").format(v));
const fmtDays = (d) => (d == null ? null : `${d} j`);

/* ───────────── Context KPIs par reason ───────────── */

function buildContextChips(sig) {
  const c = sig.context || {};
  const out = [];
  const push = (label, value) => value != null && out.push({ label, value });

  switch (sig.reason_code) {
    case "mandate_snapshot_missing":
    case "mandate_performance_review":
      push("Retard",       fmtDays(c.days_late));
      push("Cadence",      fmtDays(c.review_frequency_days));
      break;
    case "mandate_stale":
      push("Âge",          fmtDays(c.age_days));
      push("Prix",         fmtEur(c.asking_price, c.currency));
      break;
    case "mandate_expires_soon":
      push("Reste",        fmtDays(c.expires_in_days));
      break;
    case "mandate_never_published":
      push("Depuis",       fmtDays(c.commercializing_days));
      break;
    case "mandate_publication_paused":
    case "mandate_publication_dropped":
      push("Inactif",      fmtDays(c.inactive_days));
      break;
    case "mandate_views_without_leads":
      push("Vues",         fmtNum(c.views_period));
      push("Leads",        fmtNum(c.leads_period));
      push("Prix",         fmtEur(c.asking_price, c.currency));
      break;
    case "mandate_leads_without_visits":
      push("Leads",        fmtNum(c.leads_period));
      push("Visites",      fmtNum(c.visits_period));
      break;
    case "mandate_visits_without_offers":
      push("Visites",      fmtNum(c.visits_period));
      push("Offres",       fmtNum(c.offers_period));
      break;
    default:
      break;
  }
  if (sig.age_days != null && !out.some(x => x.label === "Âge")) {
    out.unshift({ label: "Âge", value: fmtDays(sig.age_days) });
  }
  return out;
}

/* ───────────── CTA : lime si action prioritaire ───────────── */

function isPrioritizedCta(sig) {
  if (sig.severity === "P0") return true;
  if (sig.severity === "P1" && (sig.context?.days_late ?? 0) >= 1) return true;
  if (sig.reason_code === "mandate_snapshot_missing") return true;
  return false;
}

/* ───────────── Row ───────────── */

function ActionRow({ signal, onOpen, testId }) {
  const sev = SEVERITY_META[signal.severity] || SEVERITY_META.P3;
  const vehicle = signal.vehicle || {};
  const vehicleLine = [vehicle.brand, vehicle.model, vehicle.version].filter(Boolean).join(" ");
  const metaBits = [
    vehicle.year ? String(vehicle.year) : null,
    fmtKm(vehicle.mileage),
  ].filter(Boolean);
  const chips = buildContextChips(signal);
  const assignedLabel = signal.assigned_to?.name || "—";
  const ctaPrimary = isPrioritizedCta(signal);
  const cta = signal.cta || { href: `/app/mandat/${signal.mandate_id}`, label: "Ouvrir le mandat" };

  return (
    <div
      className="group relative grid grid-cols-[2px_minmax(220px,2fr)_minmax(140px,1.4fr)_minmax(160px,1.1fr)_auto] items-center gap-6 border-b border-black/[0.06] px-5 py-5 transition-colors hover:bg-black/[0.015] md:grid-cols-[2px_minmax(220px,2.1fr)_minmax(180px,1.6fr)_minmax(170px,1.2fr)_minmax(110px,0.9fr)_auto]"
      data-testid={testId}
      data-reason={signal.reason_code}
      data-severity={signal.severity}
    >
      {/* 1 · Barre sévérité ultra-fine, pleine hauteur */}
      <div className={`absolute left-0 top-0 h-full w-[2px] ${sev.bar}`} />

      {/* 2 · Véhicule */}
      <div className="min-w-0 col-start-2">
        <div className="truncate text-[14.5px] font-medium tracking-[-0.005em] text-black">
          {vehicleLine || "—"}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11.5px] text-black/45">
          <span className="font-mono">{signal.mandate_reference || "—"}</span>
          {metaBits.length > 0 && <span className="text-black/25">·</span>}
          <span>{metaBits.join(" · ")}</span>
        </div>
      </div>

      {/* 3 · Reason principal */}
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-black/55">
            {sev.label}
          </span>
          <span className="truncate text-[13px] font-semibold text-black">
            {signal.title}
          </span>
        </div>
        <div className="mt-1 line-clamp-1 text-[12px] text-black/55">
          {signal.description}
        </div>
        {signal.secondary_reasons?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {signal.secondary_reasons.slice(0, 2).map(r => (
              <span key={r}
                className="rounded-full bg-black/[0.05] px-1.5 py-[1px] text-[9.5px] uppercase tracking-[0.1em] text-black/50"
                data-testid={`secondary-${r}`}>
                {r.replace(/^mandate_/, "").replace(/_/g, " ")}
              </span>
            ))}
            {signal.secondary_reasons.length > 2 && (
              <span className="text-[10px] text-black/40">
                +{signal.secondary_reasons.length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 4 · Context KPIs */}
      <div className="min-w-0">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-3">
          {chips.map(ch => (
            <div key={ch.label} className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-black/40">
                {ch.label}
              </div>
              <div className="truncate font-mono text-[12.5px] text-black">
                {ch.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5 · Commercial — hidden on <md */}
      <div className="hidden min-w-0 md:block">
        <div className="text-[9.5px] uppercase tracking-[0.14em] text-black/40">
          Commercial
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-black/70">
          {assignedLabel}
        </div>
      </div>

      {/* 6 · CTA */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => onOpen(signal, cta)}
          className={
            ctaPrimary
              ? "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#C8FF2F] px-3.5 py-1.5 text-[12px] font-medium text-black transition-transform hover:scale-[1.015]"
              : "inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-medium text-black/55 underline-offset-4 transition-colors hover:text-black hover:underline"
          }
          data-testid={`cta-${signal.mandate_id}`}
        >
          {cta.label}
          <ArrowRight size={12} className="shrink-0" />
        </button>
      </div>
    </div>
  );
}

/* ───────────── Row mobile ───────────── */

function ActionRowMobile({ signal, onOpen, testId }) {
  const sev = SEVERITY_META[signal.severity] || SEVERITY_META.P3;
  const vehicle = signal.vehicle || {};
  const vehicleLine = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
  const chips = buildContextChips(signal);
  const cta = signal.cta || { href: `/app/mandat/${signal.mandate_id}`, label: "Ouvrir" };
  const ctaPrimary = isPrioritizedCta(signal);

  return (
    <div
      className="relative border-b border-black/[0.06] px-4 py-4"
      data-testid={testId}
    >
      <div className={`absolute left-0 top-0 h-full w-[2px] ${sev.bar}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-black/55">
              {sev.label}
            </span>
            <span className="truncate text-[13.5px] font-semibold text-black">
              {signal.title}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[13px] font-medium text-black">
            {vehicleLine}
            <span className="ml-2 font-mono text-[11px] text-black/40">
              {signal.mandate_reference}
            </span>
          </div>
        </div>
        <button
          onClick={() => onOpen(signal, cta)}
          className={
            ctaPrimary
              ? "shrink-0 rounded-full bg-[#C8FF2F] px-3 py-1 text-[11.5px] font-medium text-black"
              : "shrink-0 text-[11.5px] font-medium text-black/60 underline-offset-4 hover:underline"
          }
          data-testid={`cta-mobile-${signal.mandate_id}`}
        >
          {cta.label}
        </button>
      </div>
      <div className="mt-1.5 line-clamp-2 text-[11.5px] text-black/55">
        {signal.description}
      </div>
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
          {chips.map(ch => (
            <span key={ch.label} className="text-[11px] text-black/50">
              <span className="uppercase tracking-[0.1em] text-black/40">
                {ch.label}
              </span>{" "}
              <span className="font-mono text-black/80">{ch.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────── Panel ───────────── */

export default function PilotageMandatsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState({ actions: [], totals: { P0: 0, P1: 0, P2: 0, P3: 0 }, total_mandates: 0 });
  const [loading, setLoading] = useState(true);
  const [sev, setSev] = useState("ALL");
  const [showAll, setShowAll] = useState(false);

  const load = async (severity) => {
    setLoading(true);
    try {
      const d = await pilotageMandates.actions({
        severity: severity === "ALL" ? undefined : severity,
        limit: 50,
      });
      setData(d);
    } catch (_e) {
      setData({ actions: [], totals: { P0: 0, P1: 0, P2: 0, P3: 0 }, total_mandates: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(sev); }, [sev]);

  const visible = useMemo(() => showAll ? data.actions : data.actions.slice(0, 5), [data, showAll]);
  const remaining = Math.max(0, (data.actions?.length || 0) - 5);

  const open = (_sig, cta) => {
    if (cta?.href) navigate(cta.href.startsWith("/") ? cta.href : "/app");
  };

  return (
    <section
      className="mt-16 overflow-hidden rounded-2xl bg-[#FAFAF7]"
      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)" }}
      data-testid="pilotage-mandates-panel"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black/[0.07] px-5 pt-6 pb-4 md:px-7">
        <div>
          <div className="text-[10.5px] font-medium uppercase tracking-[0.18em] text-black/45">
            Pilotage · Mandats
          </div>
          <h2 className="mt-1.5 text-[22px] font-semibold leading-none tracking-[-0.015em] text-black">
            Actions
          </h2>
          <p className="mt-1.5 text-[12.5px] text-black/55">
            Signaux commerciaux ouverts · {data.total_mandates} mandat{data.total_mandates > 1 ? "s" : ""} concerné{data.total_mandates > 1 ? "s" : ""}
          </p>
        </div>

        {/* Compteurs par sévérité */}
        <div className="flex items-center gap-1" data-testid="severity-chips">
          <SeverityChip label="Tous" active={sev === "ALL"}
                        value={data.total_mandates}
                        onClick={() => setSev("ALL")}
                        testId="chip-ALL" />
          {["P1", "P2", "P3"].map(k => (
            <SeverityChip
              key={k} label={k}
              value={data.totals?.[k] ?? 0}
              active={sev === k}
              onClick={() => setSev(k)}
              testId={`chip-${k}`}
            />
          ))}
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div className="divide-y divide-black/[0.05]" data-testid="pilotage-loading">
          {[0,1,2].map(i => (
            <div key={i} className="flex items-center gap-6 px-5 py-5 md:px-7">
              <div className="h-10 w-10 rounded-full bg-black/[0.04]" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 rounded bg-black/[0.05]" />
                <div className="h-3 w-2/3 rounded bg-black/[0.04]" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="px-7 py-14 text-center" data-testid="pilotage-empty">
          <div className="text-[14px] text-black/55">Aucune action en attente.</div>
          <button
            onClick={() => navigate("/app/mandats")}
            className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-medium text-black/60 underline-offset-4 hover:text-black hover:underline"
          >
            Voir la liste des mandats <ArrowRight size={12} />
          </button>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <>
          {/* Desktop/tablet */}
          <div className="hidden sm:block">
            {visible.map(a => (
              <ActionRow
                key={a.mandate_id + a.reason_code}
                signal={a}
                onOpen={open}
                testId={`action-row-${a.mandate_id}`}
              />
            ))}
          </div>
          {/* Mobile */}
          <div className="sm:hidden">
            {visible.map(a => (
              <ActionRowMobile
                key={a.mandate_id + a.reason_code}
                signal={a}
                onOpen={open}
                testId={`action-row-mobile-${a.mandate_id}`}
              />
            ))}
          </div>

          {remaining > 0 && (
            <div className="flex justify-center border-t border-black/[0.06] px-7 py-4">
              <button
                onClick={() => setShowAll(v => !v)}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-black/60 hover:text-black"
                data-testid="pilotage-more-btn"
              >
                {showAll ? "Réduire" : `${remaining} action${remaining > 1 ? "s" : ""} supplémentaire${remaining > 1 ? "s" : ""}`}
                <ChevronDown size={12} className={showAll ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SeverityChip({ label, value, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${
        active
          ? "bg-black text-white"
          : "bg-white text-black/60 hover:text-black"
      }`}
      style={active ? undefined : { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
      data-testid={testId}
    >
      <span className="uppercase tracking-[0.14em] text-[10.5px]">{label}</span>
      <span className="font-mono">{value}</span>
    </button>
  );
}
